import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type { ConnectedContentInventoryLaneBlockModel } from 'autogen/swagger/all';
import DebugInfo from 'components/connectedContent/inventoryLaneConnectedContent/components/DebugInfo';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';
import Picture from 'components/generic/picture/Picture';
import { Body } from 'components/generic/typography';
import PageAction from 'components/shared/Routing/PageAction';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';
import { breakpoint, sizes } from 'variables';

const Wrapper = styled(PageAction)<{ $noOfItemsMobile: number }>`
    display: ${({ $noOfItemsMobile }) => ($noOfItemsMobile <= 4 ? 'none' : 'flex')};
    position: relative;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    cursor: pointer;

    ${({ $noOfItemsMobile }) =>
        (
            $noOfItemsMobile === 2 &&
                css`
                &:nth-child(-n + 2) {
                    display: flex;
                }
            `
        ) ||
        (
            $noOfItemsMobile === 4 &&
                css`
                &:nth-child(-n + 4) {
                    display: flex;
                }
            `
        )}

    ${breakpoint.up('375px')} {
        display: flex;
    }
`;

const StyledPicture = styled(Picture)`
    margin-bottom: ${({ theme }) => theme.Spacing8};
    width: 100%;

    img {
        border-radius: ${({ theme }) => theme.RadiusMedium};
        aspect-ratio: 40/33;
        object-fit: cover;
        width: 100%;
    }
`;

const StyledBody = styled(Body)`
    text-align: center;
`;

export default function InventoryLaneItem({
    inventoryLaneBlock,
    noOfItemsMobile
}: {
    inventoryLaneBlock: ConnectedContentInventoryLaneBlockModel;
    noOfItemsMobile: number;
}) {
    const imageRef = useRef<HTMLImageElement | null>(null);

    useAdformImpressionTracking({
        ref: imageRef,
        impTrackingUrls: inventoryLaneBlock.adformTracking?.impTrackers
    });

    function trackingCallback(): void {
        if (inventoryLaneBlock.adformTracking?.clickTracking) {
            postAdformClickTracking(inventoryLaneBlock.adformTracking?.clickTracking);
        }
    }

    if (!inventoryLaneBlock.link?.pageAction) return null;

    return (
        <Wrapper
            action={inventoryLaneBlock.link?.pageAction}
            onClick={trackingCallback}
            data-datalayer={JSON.stringify(inventoryLaneBlock.dataLayer)}
            $noOfItemsMobile={noOfItemsMobile}
        >
            {!!inventoryLaneBlock.debugInfo && <DebugInfo debugInfo={inventoryLaneBlock.debugInfo} />}

            <StyledPicture ref={imageRef} {...mapResponsiveImageModelToPictureProps(inventoryLaneBlock.image, 160)} />
            <StyledBody forwardedAs="span">{inventoryLaneBlock.heading}</StyledBody>
        </Wrapper>
    );
}
